/**
 * ALT cart drawer and /cart page: Priority Order (Rush) section.
 *
 * The band itself is markup from snippets/cart-rush-section.liquid, rendered
 * in the footer slot ([data-alt-cart-rush-slot], sections/cart-drawer.liquid,
 * or the aside of sections/cart.liquid). theme.dev.js build() re-renders only
 * the line items and the price holder on a cart change, never the footer, so
 * a copy of the section also rides along inside the re-rendered line items
 * as a hidden <template data-alt-cart-rush-source> (snippets/cart-line-items
 * .liquid).
 *
 * ROOT. The script binds to ONE cart root: `#cart-drawer` when the page has
 * the drawer, else `[data-cart-page]` (the /cart page, which never renders the
 * drawer). The variable is still called `drawer` below; read it as "the cart
 * root". On /cart the same cart-items build() path re-renders the page after
 * every Rush write (Broadcast's own getCart() does exactly that on the page),
 * and if a render ever fails there the page reloads once (renderFailed()), since
 * a /cart page left showing a stale bag at checkout time is worse than a
 * flash. Nothing drawer-specific (focus return, open suppression) runs there.
 *
 * CLOCK. No calendar dates appear in the band (Carlin, 2026-10-12): the offered
 * line is server-rendered business days. The one clock decision left is which
 * added-state sentence is true (before or after the cut-off), made here from the
 * shared config (snippets/alt-priority-config.liquid, element #alt-priority-config)
 * through window.AltArrivalDates (assets/alt-arrival-dates.js), on the HUB's clock,
 * on every render, when the drawer opens and when the tab becomes visible again.
 * If either piece is missing both added-state sentences stay hidden.
 *
 * Two rules keep this small (after two review rounds found edge cases in
 * every recovery path, the 2026-09-28 cart lesson: rethink, don't patch):
 *
 * A. A Rush charge is never hidden: every Rush line is listed with the
 *    garments as its own row with a Remove (snippets/cart-rush-line.liquid,
 *    Carlin 2026-09-30), in every state, so if anything here fails the charge
 *    is still visible and removable. Nothing here retries.
 * B. Decisions come from the server's own answer, never from a render. Every
 *    write is ONE /cart/update.js request (`updates` by the bare Rush variant
 *    id to add, by line KEY to remove or fix, never a line index; the
 *    `_alt_rush_source` attribute; `sections` so the response carries the
 *    re-rendered cart). The response's cart is analysed for the next step,
 *    and its section HTML goes straight to Broadcast's own build() on the
 *    drawer's <cart-items>, so there is no second fetch to fail or arrive
 *    late. Background clean-ups read /cart.js first, so a stale render can
 *    never remove a Rush she still wants.
 *
 * Behaviour:
 * 1. sync(): on load and on every `theme:cart:change`, replace the slot's
 *    content with a clone of the re-rendered template. No template (empty
 *    bag) clears the slot.
 * 2. Add / Remove are optimistic (Carlin, 2026-09-29): the tap flips the
 *    band's data-state at once (both controls are pre-rendered; CSS shows the
 *    matching one), then one request confirms it. Taps queue: the latest
 *    intent (`wanted`) wins. Checkout is held from the tap until the
 *    confirmed render (the total then shows the real cart), enforced at
 *    submit as well, and re-applied after build() re-enables every cart
 *    button. On failure the band returns to the server state with an inline
 *    error. There is no timer anywhere in this file.
 * 3. Clean-up: when a render shows an orphan (a Rush line and no personalised
 *    line) or more than one Rush line or a quantity above 1, one background
 *    request fixes it by key. This replaces the frozen custom.js sweep, which
 *    the cart JSON now keeps idle (snippets/cart-items-json.liquid).
 * 4. A bfcache restore (Back from /cart) re-renders from the server.
 * 5. Every render goes through Broadcast's build() directly, which always
 *    dispatches theme:product:added (the drawer opens on it); for our own
 *    renders while the drawer is closed that one event is swallowed, so a
 *    background clean-up never pops the bag open.
 */
(function () {
  'use strict';

  var drawer = document.getElementById('cart-drawer') || document.querySelector('[data-cart-page]');
  if (!drawer) return;
  var onCartPage = drawer.id !== 'cart-drawer';

  /* AU/GB spelling inside the bag. assets/custom.js (frozen) converts document.body ONCE on load,
     but the drawer's content arrives later (Broadcast's build(), this file's renders, live-region
     announcements), so on /en-au the bag kept "personalized". Same lookup list and currency gate
     as custom.js, scoped to the cart root (the drawer, or the /cart page). A text node is written
     only when a word actually changes, so the observer's second pass finds nothing and stops.
     All-caps words stay all-caps. The band's spoken strings (aria-label, the live-region texts)
     are converted too: they are attributes, which a text-node walk never sees. */
  (function localizeDrawer() {
    var settings = window.theme && window.theme.settings;
    var list = settings && settings.internationalizationLookupList;
    var currencies = settings && settings.internationalizationCurrencies;
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!list || !list.length || !currencies || currencies.indexOf(active) === -1) return;
    var map = {};
    list.forEach(function (item) {
      var pair = String(item).split(':');
      if (pair.length === 2 && pair[0] && pair[1]) map[pair[0].toLowerCase()] = pair[1].toLowerCase();
    });
    var keys = Object.keys(map);
    if (!keys.length) return;
    var regex = new RegExp('\\b(' + keys.join('|') + ')\\b', 'gi');
    function swap(match) {
      var out = map[match.toLowerCase()];
      if (!out) return match;
      if (match === match.toUpperCase()) return out.toUpperCase();
      if (match[0] === match[0].toUpperCase()) return out[0].toUpperCase() + out.slice(1);
      return out;
    }
    var attributeNames = ['aria-label', 'data-error-text', 'data-added-text', 'data-removed-text'];
    function run() {
      drawer.querySelectorAll('[aria-label], [data-error-text], [data-added-text], [data-removed-text]').forEach(function (element) {
        attributeNames.forEach(function (name) {
          var value = element.getAttribute(name);
          if (!value) return;
          var next = value.replace(regex, swap);
          if (next !== value) element.setAttribute(name, next);
        });
      });
      var walker = document.createTreeWalker(drawer, NodeFilter.SHOW_TEXT, {
        acceptNode: function (node) {
          var parent = node.parentNode && node.parentNode.nodeName;
          return parent === 'SCRIPT' || parent === 'STYLE' || parent === 'TEXTAREA' ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
        }
      });
      var node;
      while ((node = walker.nextNode())) {
        var text = node.nodeValue;
        var next = text.replace(regex, swap);
        if (next !== text) node.nodeValue = next;
      }
    }
    run();
    new MutationObserver(run).observe(drawer, {childList: true, subtree: true, characterData: true});
  })();

  var JSON_HEADERS = {'Content-Type': 'application/json', Accept: 'application/json'};
  var SECTION = 'api-cart-items';

  var ids = {product: '', variant: ''};
  var server = {state: '', key: '', extras: []}; /* last confirmed Rush state */
  /* Rush state of the cart as last RENDERED by Liquid (the slot template), updated on every sync()
     whatever is in flight. It always follows the real cart, including recovery renders after a lost
     response (fail()), where server.state is deliberately left alone. The free-shipping bar keys
     off this, never off an optimistic `wanted` (Codex round 7). */
  var renderedState = '';
  var wanted = null; /* 'added' | 'offered' while the shopper's intent is unconfirmed */
  var inFlight = false;
  var carryError = false;
  var failedShape = ''; /* a clean-up that failed is not re-sent for the same cart shape */

  var live = document.createElement('span');
  live.className = 'visually-hidden';
  live.setAttribute('role', 'status');
  live.setAttribute('aria-live', 'polite');
  drawer.appendChild(live);
  var liveText = {added: '', offered: ''};

  function getSlot() {
    return drawer.querySelector('[data-alt-cart-rush-slot]');
  }

  /* The active market's timeframes from the shared config, or null (then the server's fallback
     text simply stays). Read once: the config never changes within a page. */
  var market;
  function getMarket() {
    if (market !== undefined) return market;
    market = null;
    try {
      var node = document.getElementById('alt-priority-config');
      var config = node && JSON.parse(node.textContent);
      if (config && config.markets && config.markets[config.market]) market = config.markets[config.market];
    } catch (error) {
      market = null;
    }
    return market;
  }

  /* Show the added-state sentence that matches the hub's clock: "today" before the cut-off, "next
     business day" once it has passed or on a weekend. Idempotent; runs on every render because
     sync() replaces the band with a fresh server copy. */
  function applyClock(root) {
    var dates = window.AltArrivalDates;
    var timeframes = getMarket();
    if (!root || !dates || !timeframes) return;

    var now = new Date();
    var after = dates.afterCutoff(now, timeframes.hubTz, timeframes.cutoffHour);
    if (after === null) return;

    root.setAttribute('data-cutoff', after ? 'after' : 'before');

    root.querySelectorAll('[data-alt-rush-added-line]').forEach(function (line) {
      line.hidden = line.getAttribute('data-alt-rush-added-line') !== (after ? 'after' : 'before');
    });
  }

  function getRoot() {
    var slot = getSlot();
    return slot && slot.querySelector('[data-alt-cart-rush]');
  }

  function pending() {
    return inFlight || wanted !== null;
  }

  /* While a Rush request is on its way, Checkout AND the garment rows are held. Broadcast removes
     and re-quantities garments by rendered line POSITION (theme.dev.js updateCart), so a garment
     tap landing after a Rush add or removal has shifted the positions would hit the wrong line.
     `inert` on the items holder blocks pointer and keyboard use of every control in it; the
     holder element itself survives build() (only its innerHTML is replaced), so the hold does. */
  function holdCheckout(hold) {
    var itemsHolder = drawer.querySelector('[data-items-holder]');
    if (itemsHolder) itemsHolder.inert = !!hold;
    drawer.querySelectorAll('.cart__checkout, .additional-checkout-buttons button').forEach(function (button) {
      if (hold) {
        if (!button.disabled) button.setAttribute('data-alt-rush-hold', '');
        button.disabled = true;
      } else if (button.hasAttribute('data-alt-rush-hold')) {
        button.removeAttribute('data-alt-rush-hold');
        button.disabled = false;
      }
    });
  }

  function release() {
    if (!pending()) holdCheckout(false);
  }

  /* Every request or render ends here: send a tap queued meanwhile, else start any clean-up the
     rendered cart needs, else release Checkout. A clean-up's own completion never starts another
     clean-up (settleAfterCleanUp), so a clean-up that could do nothing cannot loop. */
  function settle(allowCleanUp) {
    inFlight = false;
    if (wanted) {
      reconcile();
      return;
    }
    var root = getRoot();
    if (allowCleanUp !== false && root && root.getAttribute('data-needs-cleanup') === 'true') {
      cleanUp();
      return;
    }
    release();
  }

  /* Rush includes free express shipping (Carlin, 2026-10-01), so while the rendered bag holds a
     Rush line (renderedState 'added': the line plus a personalised piece) the free-shipping bar is
     full and shows its qualified message whatever the subtotal. Broadcast recomputes the bar from
     the subtotal inside build() after dispatching theme:cart:change (freeShippingMessageHandle,
     updateProgress), so this runs in a microtask after build(), and on window load after
     Broadcast's constructor. When Rush goes, Broadcast's own pass has already drawn the real bar,
     so there is nothing to undo. snippets/free-shipping.liquid applies the same rule at first paint. */
  function syncFreeShipping() {
    var rush = renderedState === 'added';
    /* The asterisk (US site only, rendered by the snippet) shows only while Rush is in the bag. */
    document.querySelectorAll('[data-alt-rush-free-mark]').forEach(function (mark) {
      mark.hidden = !rush;
    });
    if (!rush) return;
    document.querySelectorAll('[data-free-shipping="true"]').forEach(function (bar) {
      bar.classList.add('is-success');
      var progress = bar.querySelector('[data-progress-bar]');
      if (progress) progress.value = 100;
    });
  }

  function setState(root, state) {
    if (!root || !state) return;

    root.setAttribute('data-state', state);
    var error = root.querySelector('[data-alt-cart-rush-error]');
    if (error) error.hidden = true;
  }

  /* The Rush line rows in the item list (snippets/cart-rush-line.liquid). While a Remove is on
     its way they are hidden at once, and kept hidden through any re-render until the server
     confirms; a failed Remove re-renders them from the server. */
  function hideRushLines(hide) {
    drawer.querySelectorAll('[data-alt-cart-rush-line]').forEach(function (row) {
      row.classList.toggle('is-removing', hide);
    });
  }

  /* Show the shopper's intent on the band. Removing Rush in a bag whose personalised pieces are all
     pre-order (no offer, snippets/cart-rush-section.liquid data-offer-eligible) hides the band
     instead of flashing an Add that must not be tapped. */
  function showWanted(root) {
    if (!root || !wanted) return;
    if (wanted === 'offered' && root.getAttribute('data-offer-eligible') !== 'true') {
      root.hidden = true;
      return;
    }
    setState(root, wanted);
  }

  /* After a Remove, keep focus in the drawer: the band's Add when it is offered, else the drawer's
     close button (the removed control itself is gone). */
  function focusAfterRemove(root) {
    var add = root && !root.hidden && root.querySelector('[data-alt-cart-rush-add]');
    var target = add || drawer.querySelector('[data-cart-drawer-close], .drawer__close-button, [data-drawer-close]');
    if (target && typeof target.focus === 'function') target.focus();
  }

  function showError(root) {
    var error = root && root.querySelector('[data-alt-cart-rush-error]');
    if (!error) return;

    error.textContent = root.getAttribute('data-error-text') || '';
    error.hidden = false;
  }

  function isPersonalised(item) {
    var props = item.properties || {};
    return String(props['Embroidery text'] || '').trim() !== '' || String(props.Initials || '').trim() !== '';
  }

  /* The Rush facts of a /cart.js-shaped cart: state, the line to keep (the first bare one,
     else the first), its quantity, and every other Rush line. */
  function analyse(cart) {
    var items = (cart && cart.items) || [];
    var rush = items.filter(function (item) { return String(item.product_id) === String(ids.product); });
    var personalised = items.some(function (item) { return String(item.product_id) !== String(ids.product) && isPersonalised(item); });

    var keep = rush.filter(function (item) { return !item.properties || Object.keys(item.properties).length === 0; })[0] || rush[0];
    var state = rush.length ? (personalised ? 'added' : 'orphan') : (personalised ? 'offered' : 'hidden');

    return {
      state: state,
      key: keep ? keep.key : '',
      qty: keep ? keep.quantity : 0,
      extras: rush.filter(function (item) { return item !== keep; }).map(function (item) { return item.key; }),
      shape: rush.map(function (item) { return item.key + ':' + item.quantity; }).join(',') + '|' + personalised
    };
  }

  function send(updates, source) {
    var body = {updates: updates, sections: SECTION};
    if (typeof source === 'string') body.attributes = {_alt_rush_source: source};

    return fetch('/cart/update.js', {method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body)})
      .then(function (response) {
        if (!response.ok) throw new Error('rush update ' + response.status);
        return response.json();
      });
  }

  /* Broadcast's build() always ends by dispatching theme:product:added, and the drawer opens on
     that event (theme.dev.js openCartDrawerOnProductAdded), so any re-render would pop a closed
     bag open (the 2026-09-28 watchdog did exactly that). During OUR build() only, and only while
     the drawer is closed, that one event is swallowed before the drawer's document listener. */
  var suppressOpen = false;
  window.addEventListener('theme:product:added', function (event) {
    if (!suppressOpen) return;
    event.stopImmediatePropagation();
  }, true);

  function drawerIsOpen() {
    var cartDrawer = document.querySelector('cart-drawer');
    return !!(cartDrawer && cartDrawer.cartDrawerIsOpen);
  }

  /* Give section HTML to Broadcast's own build() on the drawer's <cart-items> (what getCart()
     does after its fetch). build() is synchronous and ends in theme:cart:change, so sync() has
     run by the time this returns true. */
  function build(html) {
    var items = drawer.querySelector('cart-items');
    if (!html || !items || typeof items.build !== 'function') return false;

    var holder = document.createElement('div');
    holder.innerHTML = html;
    var clean = holder.querySelector('[data-api-content]');
    if (!clean) return false;

    suppressOpen = !onCartPage && !drawerIsOpen();
    try {
      items.build(clean);
    } finally {
      suppressOpen = false;
    }
    return true;
  }

  /* A render that could not happen. The drawer just keeps its previous contents until the next
     change; the /cart page reloads instead (once per tab session: a render that fails every time
     must not become a reload loop; a successful build() clears the guard). */
  var RELOAD_KEY = 'alt-cart-rush-reloaded';
  function renderFailed(error) {
    console.warn('[alt-cart-rush] cart re-render failed; the bag may show the previous cart until it next changes', error);
    if (!onCartPage) return;
    try {
      if (window.sessionStorage.getItem(RELOAD_KEY)) return;
      window.sessionStorage.setItem(RELOAD_KEY, '1');
    } catch (storageError) {
      return; /* no storage: never risk a loop */
    }
    window.location.reload();
  }

  /* Render the confirmed cart: from the response when it carries the section, otherwise from our
     own section fetch (never a theme:cart:refresh we cannot follow). Resolves once rendered, or
     once rendering is known to have failed; the Checkout hold is released only after that. */
  function render(cart) {
    if (build(cart && cart.sections && cart.sections[SECTION])) return Promise.resolve();

    return fetch('/cart?section_id=' + SECTION, {headers: {Accept: 'text/html'}})
      .then(function (response) {
        if (!response.ok) throw new Error('rush section ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;
        var content = holder.querySelector('[data-api-content]');
        if (!build(content ? content.outerHTML : '')) throw new Error('rush section had no content to build');
      })
      .catch(renderFailed);
  }

  function sync() {
    var slot = getSlot();
    if (!slot) return;

    var source = drawer.querySelector('[data-alt-cart-rush-source]');
    var hadFocus = slot.contains(document.activeElement);
    var keepError = carryError;
    carryError = false;

    if (source && source.content) {
      slot.replaceChildren(source.content.cloneNode(true));
    } else {
      slot.replaceChildren();
    }

    var root = getRoot();
    applyClock(root);
    if (root) {
      ids.product = root.getAttribute('data-rush-product-id') || ids.product;
      ids.variant = root.getAttribute('data-rush-variant-id') || ids.variant;
      liveText.added = root.getAttribute('data-added-text') || liveText.added;
      liveText.offered = root.getAttribute('data-removed-text') || liveText.offered;
    }

    var state = root ? root.getAttribute('data-state') : 'hidden';
    renderedState = state;

    /* The free-express footnote in the checkout note (US site only, sections/cart-drawer.liquid)
       shows while the Rush section, and so an asterisk, is on screen. */
    drawer.querySelectorAll('[data-alt-rush-footnote]').forEach(function (note) {
      note.hidden = !(state === 'added' || state === 'offered');
    });
    if (!inFlight) {
      server.state = state;
      server.key = root ? root.getAttribute('data-rush-line-key') || '' : '';
      server.extras = root ? (root.getAttribute('data-rush-extra-keys') || '').split(' ').filter(Boolean) : [];
    }

    if (wanted && (state === 'added' || state === 'offered')) showWanted(root);
    hideRushLines(wanted === 'offered');
    if (keepError) showError(root);

    if (hadFocus && root) {
      var next = root.querySelector(root.getAttribute('data-state') === 'added' ? '[data-alt-cart-rush-remove]' : '[data-alt-cart-rush-add]');
      if (next) next.focus();
    }

    if (!inFlight && wanted) {
      reconcile();
    } else if (!inFlight && root && root.getAttribute('data-needs-cleanup') === 'true') {
      cleanUp();
    }
  }

  /* Bring the server in line with `wanted`, one request at a time. */
  function reconcile() {
    if (inFlight || !wanted) return;

    if (server.state === wanted) {
      wanted = null;
      release();
      return;
    }

    var target = wanted;
    var updates = {};
    if (target === 'added') {
      if (!ids.variant) return fail();
      updates[ids.variant] = 1;
    } else {
      if (!server.key) return fail();
      updates[server.key] = 0;
      /* Remove means no Rush at all: every Rush line goes. */
      server.extras.forEach(function (key) { updates[key] = 0; });
    }

    inFlight = true;
    holdCheckout(true);

    send(updates, target === 'added' ? 'cart' : '')
      .then(function (cart) {
        var facts = analyse(cart);
        inFlight = false;
        server.state = facts.state;
        server.key = facts.key;
        server.extras = facts.extras;
        live.textContent = liveText[target] || '';

        if (wanted && wanted !== target) {
          /* She tapped again mid-flight: send the follow-up before rendering. */
          reconcile();
          return;
        }

        wanted = null;
        inFlight = true; /* keep Checkout held until the confirmed cart is on screen */
        render(cart).then(function () { settle(); });
      })
      .catch(function () {
        inFlight = false;
        fail();
      });
  }

  function fail() {
    wanted = null;
    carryError = true;

    var root = getRoot();
    if (root) {
      if (server.state === 'added' || server.state === 'offered') root.hidden = false;
      setState(root, server.state);
      showError(root);
    }
    /* An orphan's band is hidden and has no visible error: say it in the live region. */
    if (!root || root.hidden) live.textContent = (root && root.getAttribute('data-error-text')) || '';
    /* Re-render from the server (the write may have landed even if its response was lost), and
       keep Checkout held until the real cart is on screen. */
    inFlight = true;
    render(null).then(function () { settle(); });
  }

  /* Orphan, duplicate or quantity clean-up, decided on a fresh /cart.js read. */
  function cleanUp() {
    var before = null; /* line keys in the read that justified the clean-up */
    inFlight = true;
    holdCheckout(true);

    fetch('/cart.js', {headers: {Accept: 'application/json'}})
      .then(function (response) {
        if (!response.ok) throw new Error('rush cart read ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        var facts = analyse(cart);
        var updates = {};

        if (facts.shape === failedShape) return null;
        before = {};
        ((cart && cart.items) || []).forEach(function (item) { before[item.key] = true; });

        if (facts.state === 'orphan') {
          updates[facts.key] = 0;
          facts.extras.forEach(function (key) { updates[key] = 0; });
        } else if (facts.state === 'added') {
          if (facts.qty > 1) updates[facts.key] = 1;
          facts.extras.forEach(function (key) { updates[key] = 0; });
        }
        if (!Object.keys(updates).length) return null;

        return send(updates, facts.state === 'orphan' ? '' : undefined).catch(function (error) {
          failedShape = facts.shape;
          throw error;
        });
      })
      .then(function (cart) {
        /* A rushed PDP add can land between the read and the write, and merge into the Rush line
           just removed. A personalised line that is NEW since the read and carries Rush: Yes asked
           for Rush, so put one bare Rush line back. Only new lines count: an old stamp never
           re-adds a Rush she removed. */
        var facts = cart && analyse(cart);
        var crossed = facts && facts.state === 'offered' && ((cart.items) || []).some(function (item) {
          return before && !before[item.key] && isPersonalised(item) && item.properties && item.properties.Rush === 'Yes';
        });
        if (crossed && ids.variant) {
          var restore = {};
          restore[ids.variant] = 1;
          return send(restore, 'pdp');
        }
        return cart;
      })
      .then(function (cart) {
        return cart ? render(cart) : null;
      })
      .then(function () { settle(false); })
      .catch(function (error) {
        console.warn('[alt-cart-rush] Rush clean-up did not complete; the Rush row stays visible and removable', error);
        inFlight = false;
        if (wanted) {
          reconcile();
        } else {
          release();
        }
      });
  }

  function onClick(event) {
    var button = event.target.closest && event.target.closest('[data-alt-cart-rush-add], [data-alt-cart-rush-remove]');
    if (!button) return;

    var root = button.closest('[data-alt-cart-rush]');
    if (!root) return;

    event.preventDefault();

    var hadFocus = button === document.activeElement;
    wanted = button.hasAttribute('data-alt-cart-rush-add') ? 'added' : 'offered';
    showWanted(root);
    hideRushLines(wanted === 'offered');

    if (hadFocus) {
      if (wanted === 'added') {
        var remove = root.querySelector('[data-alt-cart-rush-remove]');
        if (remove) remove.focus();
      } else {
        focusAfterRemove(root);
      }
    }

    holdCheckout(true);
    reconcile();
  }

  /* Remove on a Rush line in the item list: the same removal as the footer's Remove (every Rush
     line, by key, one request), so it never goes through Broadcast's positional updateCart. */
  function onLineRemove(event) {
    var button = event.target.closest && event.target.closest('[data-alt-cart-rush-line-remove]');
    if (!button) return;

    event.preventDefault();

    var hadFocus = button === document.activeElement;
    wanted = 'offered';
    var root = getRoot();
    if (root && root.getAttribute('data-state') === 'added') showWanted(root);
    hideRushLines(true);
    if (hadFocus) focusAfterRemove(root);

    holdCheckout(true);
    reconcile();
  }

  var slot = getSlot();
  if (slot) slot.addEventListener('click', onClick);
  drawer.addEventListener('click', onLineRemove);

  document.addEventListener('theme:cart:change', function () {
    /* A build() just succeeded, so the page can render again: re-arm the /cart reload guard. (Not in
       sync(), which also runs at load: a render that fails on every load would then loop.) */
    try {
      window.sessionStorage.removeItem(RELOAD_KEY);
    } catch (error) {
      /* storage unavailable: nothing to clear */
    }
    sync();

    /* Broadcast's build() re-enables every cart button after dispatching this event;
       re-apply the hold once build() has finished (a microtask, not a timer). */
    queueMicrotask(function () {
      if (pending()) holdCheckout(true);
      syncFreeShipping();
    });
  });

  /* The hold is enforced at submit too, whatever the button's disabled flag says. */
  drawer.addEventListener('submit', function (event) {
    if (!pending()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
  drawer.addEventListener('click', function (event) {
    if (!pending() || !event.target.closest || !event.target.closest('.cart__checkout, .additional-checkout-buttons')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  /* The hub's clock keeps moving while the page sits open: re-read it when the bag is opened and
     when the tab comes back (no timer). */
  document.addEventListener('theme:cart-drawer:open', function () {
    applyClock(getRoot());
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) applyClock(getRoot());
  });

  /* Back from /cart restores this page from bfcache with an old band and total: re-read the cart. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && !inFlight) render(null);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
  if (document.readyState === 'complete') {
    syncFreeShipping();
  } else {
    window.addEventListener('load', syncFreeShipping);
  }
})();
